import React, { useEffect, useState } from 'react';
import FlippexLoader from '../components/ui/FlippexLoader';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { api } from '../context/AuthContext';
import { ArrowRight, Buildings } from '@phosphor-icons/react';
import { PortfolioValueHeader } from '../components/investor/PortfolioValueHeader';
import { PayoutHistory } from '../components/investor/PayoutHistory';
import { InvestmentCard, OrderCard } from '../components/investor/InvestmentCard';
import { money, pct, changeColor, shortDate, INVESTOR_BLUE } from '../components/investor/investorFormat';

const ALLOCATION_VIEWS = [
  { key: 'project', label: 'By project', field: 'project_title' },
  { key: 'strategy', label: 'By strategy', field: 'strategy' },
  { key: 'location', label: 'By location', field: 'location' },
];

export default function PortfolioPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('project');

  useEffect(() => {
    api.get('/portfolio/v2/home')
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-40">
        <FlippexLoader size={48} animate={false} />
      </div>
    );
  }

  const portfolio = data?.portfolio || {};
  const holdings = data?.holdings || [];
  const orders = data?.orders || [];

  if (holdings.length === 0 && orders.length === 0) {
    return (
      <div className="container mx-auto px-5 md:px-8 max-w-[1100px] py-16 md:py-24 text-center" data-testid="portfolio-empty">
        <h1 className="text-2xl md:text-3xl font-heading">Your portfolio starts here</h1>
        <p className="text-base text-foreground-muted mt-3 max-w-md mx-auto">
          Build a diversified real estate portfolio from carefully selected opportunities.
        </p>
        <Button className="mt-7 rounded-full px-6" onClick={() => navigate('/app/explore')} data-testid="portfolio-explore-cta">
          Explore investments <ArrowRight size={15} weight="bold" className="ml-1.5" />
        </Button>
      </div>
    );
  }

  const field = ALLOCATION_VIEWS.find((v) => v.key === view).field;
  const groups = holdings.reduce((acc, h) => {
    const key = h[field] || 'Other';
    acc[key] = (acc[key] || 0) + h.current_value;
    return acc;
  }, {});
  const total = Object.values(groups).reduce((a, b) => a + b, 0) || 1;
  const allocation = Object.entries(groups)
    .map(([label, value]) => ({ label, value, percent: (value / total) * 100 }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="container mx-auto px-5 md:px-8 max-w-[1100px] py-8 md:py-12 space-y-8 md:space-y-10" data-testid="portfolio-page">
        <PortfolioValueHeader portfolio={portfolio} valuation={holdings[0]} title="Portfolio" />

        <PayoutHistory />

        {/* Allocation */}
        <section className="bg-white rounded-2xl border border-black/[0.05] px-5 py-5" data-testid="portfolio-allocation">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-sm font-medium">Portfolio allocation</h2>
            <div className="flex items-center gap-1 rounded-lg bg-slate-100/80 p-0.5">
              {ALLOCATION_VIEWS.map((v) => (
                <button
                  key={v.key}
                  onClick={() => setView(v.key)}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                    view === v.key ? 'bg-white shadow-sm font-medium' : 'text-foreground-muted'
                  }`}
                  data-testid={`allocation-view-${v.key}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 h-2.5 rounded-full overflow-hidden flex bg-slate-100">
            {allocation.map((a, i) => (
              <div
                key={a.label}
                className="h-full"
                style={{
                  width: `${a.percent}%`,
                  backgroundColor: INVESTOR_BLUE,
                  opacity: 1 - i * 0.18,
                }}
              />
            ))}
          </div>

          <div className="mt-4 space-y-2.5">
            {allocation.map((a) => (
              <div key={a.label} className="flex items-center justify-between text-sm">
                <span className="truncate text-slate-900 capitalize">{a.label}</span>
                <span className="text-foreground-muted tabular-nums ml-4 flex-shrink-0">
                  {money(a.value)} · {a.percent.toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Desktop investment list */}
        <section className="hidden md:block bg-white rounded-2xl border border-black/[0.05] overflow-hidden" data-testid="portfolio-table">
          <div className="px-5 py-4 border-b border-black/[0.05]">
            <h2 className="text-sm font-medium">Your investments</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-foreground-muted text-xs">
                  <th className="text-left font-medium px-5 py-2.5">Investment</th>
                  <th className="text-right font-medium px-3 py-2.5">Invested</th>
                  <th className="text-right font-medium px-3 py-2.5">Current value</th>
                  <th className="text-right font-medium px-3 py-2.5">Change</th>
                  <th className="text-right font-medium px-3 py-2.5">Received</th>
                  <th className="text-left font-medium px-3 py-2.5">Next event</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {holdings.map((h) => (
                  <tr
                    key={h.security_id}
                    onClick={() => navigate(`/app/portfolio/${h.security_id}`)}
                    className="border-t border-black/[0.05] hover:bg-slate-50/70 cursor-pointer"
                    data-testid={`portfolio-row-${h.security_id}`}
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        {h.image ? (
                          <img
                            src={h.image}
                            alt={h.project_title}
                            className="h-10 w-10 rounded-lg object-cover flex-shrink-0"
                            data-testid={`portfolio-row-image-${h.security_id}`}
                          />
                        ) : (
                          <span className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                            <Buildings size={16} className="text-slate-400" weight="duotone" />
                          </span>
                        )}
                        <span className="min-w-0">
                          <p className="font-medium truncate">{h.project_title}</p>
                          <p className="text-xs text-foreground-muted truncate">{h.location}</p>
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3.5 text-right tabular-nums">{money(h.invested)}</td>
                    <td className="px-3 py-3.5 text-right tabular-nums font-medium">{money(h.current_value)}</td>
                    <td className={`px-3 py-3.5 text-right tabular-nums ${changeColor(h.unrealized_percent)}`}>
                      {pct(h.unrealized_percent, { signed: true })}
                    </td>
                    <td className="px-3 py-3.5 text-right tabular-nums">{h.income_received ? money(h.income_received) : '—'}</td>
                    <td className="px-3 py-3.5 text-foreground-muted text-xs">
                      {h.target_exit_date ? `Target exit ${shortDate(h.target_exit_date)}` : '—'}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <ArrowRight size={14} className="text-foreground-muted inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Mobile investment cards */}
        <div className="md:hidden space-y-3" data-testid="portfolio-cards">
          {holdings.map((h) => <InvestmentCard key={h.security_id} holding={h} />)}
        </div>

        {orders.length > 0 && (
          <section className="space-y-3">
            <p className="text-[11px] uppercase tracking-[0.16em] text-foreground-muted">In progress</p>
            {orders.map((order) => <OrderCard key={order.id} order={order} />)}
          </section>
        )}
    </div>
  );
}
